import React, { useContext, useEffect } from "react";
import axios from "axios";
import { base_url } from "../../config";
import RegistrationContext from "../../contexts/RegistrationContext";

export const Documents = () => {
  const { data, setData, setError } = useContext(RegistrationContext);

  const anyInProgress = Array.isArray(data.files)
    && data.files.some((f: any) => !f.size || f.size === 0);

  useEffect(() => {
    if (!anyInProgress) return;
    const timer = setInterval(async () => {
      try {
        const r = await axios.get(`${base_url}/api/progress`);
        const files = r.data?.data?.files;
        if (Array.isArray(files)) {
          setData((prev: any) => ({ ...prev, files }));
        }
      } catch {
        /* swallow: another tick will retry */
      }
    }, 4000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyInProgress]);

  const SingleItem = ({
    title,
    type,
    size,
    endpoint,
  }: {
    title: string;
    type: string;
    size: number;
    endpoint: string;
  }) => {
    function formatBytes(bytes: number): string {
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    }

    const inProgress = !size || size === 0;

    const handleClick = async () => {
      try {
        const response = await axios.get(`${base_url}/${endpoint}`, {
          responseType: "blob",
        });
        const fileURL = window.URL.createObjectURL(new Blob([response.data]));
        const fileName = title + ".pdf";
        const link = document.createElement("a");
        link.href = fileURL;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error: any) {
        setError(
          "Unexpected error. " + error.response?.data?.message || error.message
        );
      }
    };

    return (
      <div className="flex items-center justify-between rounded-lg border p-4">
        <div className="flex items-center gap-3">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={`h-5 w-5 ${inProgress ? "text-gray-400" : "text-blue-600"}`}
          >
            <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" x2="8" y1="13" y2="13" />
            <line x1="16" x2="8" y1="17" y2="17" />
            <line x1="10" x2="8" y1="9" y2="9" />
          </svg>
          <div>
            <p className="font-medium">{title} </p>
            <p className="text-sm text-gray-500">
              {type} •{" "}
              {inProgress ? (
                <span className="italic text-amber-600">(in progress)</span>
              ) : (
                formatBytes(size)
              )}
            </p>
          </div>
        </div>
        <button
          onClick={() => handleClick()}
          disabled={inProgress}
          className="rounded-md border border-gray-200 px-3 py-1 text-sm font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
        >
          Download
        </button>
      </div>
    );
  };

  return (
    <div className="rounded-lg border bg-white p-6 shadow-sm" id="documents">
      <h2 className="mb-4 text-xl font-bold">Documents</h2>
      {anyInProgress && (
        <p className="mb-3 text-xs text-amber-700">
          Some documents are still being generated. They will become available
          within a few seconds.
        </p>
      )}
      <div className="space-y-3">
        {data.files.map((file: any) => {
          return (
            <SingleItem
              key={file.id}
              type={file.type}
              size={file.size}
              title={file.title}
              endpoint={file.endpoint}
            />
          );
        })}
      </div>
    </div>
  );
};
